import React from 'react';
import {useTranslation} from 'react-i18next';
import {formatDate} from '../../agency/v2/AlertsPopover';
import {
    WEBHOOK_STATE_FAILING,
    WEBHOOK_STATE_LOADING,
    WEBHOOK_STATE_NONE,
    WEBHOOK_STATE_OK,
    WEBHOOK_STATE_UNAVAILABLE,
} from './webhookModel';

/** El nombre de un destino para la pantalla (`slack` → «Slack»). Las marcas no se traducen. */
export function destinationName(destination, t) {
    const names = {slack: 'Slack', gchat: 'Google Chat', make: 'Make', zapier: 'Zapier'};
    if (names[destination]) return names[destination];
    return destination === 'other' ? t('alerts_center.webhook_setup.dest_other') : null;
}

/**
 * El webhook en una línea, a la derecha de los filtros de «Mis reglas» (UD-18): «● Webhook conectado · Cambiar»,
 * «● Webhook sin conectar · Conectar», «● El webhook falla desde el 12/10 · Arreglar». Los tres abren el mismo panel de
 * 3 pasos, que explica el detalle. Es el ancla `#webhook` de los links de Plataformas.
 */
export function WebhookStatusInline({status, canEdit = true, onOpen}) {
    const {t, i18n} = useTranslation();
    const state = status ? status.state : WEBHOOK_STATE_LOADING;
    if (state === WEBHOOK_STATE_LOADING) return null;
    const dest = status ? destinationName(status.destination, t) : null;
    let text;
    let note = null;
    let actionKey = null;
    let testId = null;
    if (state === WEBHOOK_STATE_OK) {
        text = dest ? t('alerts_center.webhook_status.title_ok_dest', {dest, interpolation: {escapeValue: false}})
            : t('alerts_center.webhook_status.title_ok');
        // Conectado pero con envíos que fallaron en la semana: se sigue diciendo (lo decía la tarjeta, C-25).
        if (status.last7d && status.last7d.failed > 0) note = t('alerts_center.webhook_status.failed_7d', {count: status.last7d.failed});
        actionKey = 'alerts_center.webhook_status.change';
        testId = 'alc-webhook-change';
    } else if (state === WEBHOOK_STATE_NONE) {
        text = t('alerts_center.webhook_status.title_none');
        actionKey = 'alerts_center.webhook_status.connect';
        testId = 'alc-webhook-connect';
    } else if (state === WEBHOOK_STATE_FAILING) {
        const since = formatDate(status.since, i18n.language);
        text = since ? t('alerts_center.webhook_status.title_failing_since', {since})
            : t('alerts_center.webhook_status.title_failing');
        actionKey = 'alerts_center.webhook_status.fix';
        testId = 'alc-webhook-fix';
    } else if (state === WEBHOOK_STATE_UNAVAILABLE) {
        text = t('alerts_center.mr.webhook_unavailable');
    } else {
        text = t('alerts_center.webhook.load_error');
    }
    return (
        <span id="webhook" tabIndex={-1} className={`alc-hook -${state}`} data-testid="alc-webhook-status" data-state={state}>
            <span className="alc-hook__dot" aria-hidden="true"/>
            <span className="alc-hook__text">{text}</span>
            {note && <span className="alc-hook__note" data-testid="alc-webhook-failed">{note}</span>}
            {actionKey && canEdit && (
                <>
                    <span aria-hidden="true">·</span>
                    <button type="button" className="alc-link -button" onClick={onOpen} data-testid={testId}>{t(actionKey)}</button>
                </>
            )}
        </span>
    );
}

export default WebhookStatusInline;
